import React, { useRef, useState, useEffect, useCallback } from "react";
import SideBar from "../../components/bars/SideBar";
import HomePannel from "../../components/bars/HomePannel";
import ChatLayout from "../../layouts/ChatLayout";
import ChatHeader from "../../components/chat/ChatHeader";
import ChatView from "../../components/chat/ChatView/ChatView";
import ChatInput from "../../components/chat/ChatInput/ChatInput";
import styles from "../../style/Pages/ChatPage.module.css";
import useChatHome from "../../hooks/useChatHome";
import {
  getSelectedPerson,
  getUserDetails,
  storeSelectedPerson,
} from "../../utils/auth";
import NoConvFound from "../../components/ui/NoDataFound/NoConvFound";
import { useHomeChat } from "../../hooks/useHome";
import ViewChannelPopUp from "./PopUp/ViewChannelPopUp";
import ViewGroupPopUp from "./PopUp/ViewGroupPopUp";
import PersonQuickView from "./PopUp/PersonQuickView";
import WebSocketService from "../../services/websocket";
import WebSocketOnline from "../../services/onlineSocketService";
import { fetchSelectedPeople } from "../../services/commonServices";
import { useInfiniteMessagesHome } from "../../hooks/useInfiniteMessages";
import { useInView } from "react-intersection-observer";

const HomePage = () => {
  const chatViewRef = useRef(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [height, setHeight] = useState(40);
  const [refreshChat, setRefreshChat] = useState(0);
  const [showChannelPopup, setShowChannelPopup] = useState(false);
  const [showGroupPopup, setShowGroupPopup] = useState(false);
  const [showPersonPopUp, setShowPersonPopUp] = useState(false);
  const [skipLoader, setSkipLoader] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const selectedUser = getSelectedPerson();
  const currentChatRef = useRef(selectedUser?.chat_unique);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const selectedUserRef = useRef(selectedUser);
  const userName =
    selectedUser?.type === "channel"
      ? selectedUser?.channel_name
      : selectedUser?.name;
  const userDetails = getUserDetails() || {};
  const channelId = selectedUser?.id; // Assuming this is available
  const groupId = selectedUser?.group_id;
  const userId = selectedUser?.register_id;
  const BASE_URL = process.env.REACT_APP_API_URL;
  const onlineStatus = selectedUser?.online_status;
  const userImage = selectedUser?.emp_pic;

  const loadingRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const scrollPositionRef = useRef(0);
  const previousMessagesLengthRef = useRef(0);
  const isScrollingProgrammaticallyRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);

  const {
    text,
    setText,
    uploadedFiles,
    inputRef,
    handleFileChange,
    handleRemoveFile,
    handleSend,
    typing,
    handleChange,
    clearSendingMessage,
    reactions,
    clearRecivingMessage,
    insertMentionUser,
    sendingmessage: chatSendingMessage, // Renamed
    recivingMessage: recivedMessageSocket,
    pendingMessages,
    setPendingMessages,
    setUploadedFiles, // Add this if it's available from useChatHome
    setTyping,
    // Get pending messages from useChat
  } = useChatHome(refreshChat);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loadingConv,
    // error: messagesError,
    refetch: refetchMessages,
  } = useInfiniteMessagesHome(debouncedSearchQuery);

  useEffect(() => {
    if (selectedUser?.chat_unique) {
      // This will trigger the query since enabled is true when chat_unique exists
      refetchMessages();
    }
  }, [selectedUser?.chat_unique, refetchMessages]);
  const { channels, directMessages, loading, error } = useHomeChat(
    refreshCount,
    skipLoader
  );

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      clearSendingMessage(); // clear unsent messages
      clearRecivingMessage(); // clear incoming messages
    },
    [clearRecivingMessage, clearSendingMessage]
  );

  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  }, [selectedUser?.chat_unique]);

  let query = "";
  if (selectedUser) {
    if (selectedUser.is_group_chat === "0") {
      query = `register_id=${selectedUser.register_id}`;
    } else if (selectedUser.is_group_chat === "1") {
      query = `group_id=${selectedUser.group_id}`;
    } else if (selectedUser.is_group_chat === "channel") {
      query = `channel_id=${selectedUser.id}`;
    }
  }

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    const updateSelectedUserFromApi = async () => {
      if (!selectedUserRef.current) return;

      try {
        let updatedData = null;
        const res = await fetchSelectedPeople(`?${query}`);

        if (selectedUserRef.current.is_group_chat === "channel") {
          updatedData = {
            ...selectedUserRef.current,
            channel_name: res?.channel_details[0]?.channel_name,
            allow_access:
              res?.identify_active_deactive.channel_details[0]?.allow_access,
            channel_manager:
              res?.identify_active_deactive.channel_details[0]?.channel_manager,
          };
        } else if (selectedUserRef.current.is_group_chat === "1") {
          updatedData = {
            ...selectedUserRef.current,
            name: res?.identify_active_deactive.group_details[0]?.group_name,
            emp_pic: res?.identify_active_deactive.group_details[0]?.group_icon,
            group_status:
              res?.identify_active_deactive?.group_details[0]?.group_status,
          };
        } else if (selectedUserRef.current.is_group_chat === "0") {
          updatedData = {
            ...selectedUserRef.current,
            name: res?.identify_active_deactive?.employee_details[0]?.name,
            emp_pic:
              res?.identify_active_deactive?.employee_details[0]?.emp_pic,
            active_status:
              res?.identify_active_deactive?.employee_details[0]?.active_status,
          };
        }

        if (
          updatedData &&
          JSON.stringify(updatedData) !==
            JSON.stringify(selectedUserRef.current)
        ) {
          storeSelectedPerson(updatedData);
        }
      } catch (error) {}
    };

    if (query) updateSelectedUserFromApi();
  }, [query]);

  const getMessageTimestamp = useCallback((date, time) => {
    try {
      const [timePart, period] = time.split(" ");
      let [hours, minutes, seconds = 0] = timePart.split(":").map(Number);

      // Convert to 24-hour format
      if (period === "PM" && hours < 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      const dateObj = new Date(date);
      dateObj.setHours(hours, minutes, seconds, 0);
      return dateObj.getTime();
    } catch (error) {
      return new Date().getTime();
    }
  }, []);
  // const filterMessagesByCurrentChat = useCallback((messages) => {
  //   if (!messages || !Array.isArray(messages)) return [];
  //   return messages.filter((msg) => msg.chat_unique === currentChatRef.current);
  // }, []);
  // const filteredSocketMessages = React.useMemo(() => {
  //   return filterMessagesByCurrentChat(recivedMessageSocket || []);
  // }, [recivedMessageSocket, filterMessagesByCurrentChat]);

  // const filteredSendingMessages = React.useMemo(() => {
  //   return filterMessagesByCurrentChat(chatSendingMessage || []);
  // }, [chatSendingMessage, filterMessagesByCurrentChat]);
  // Filter socket messages by current chat

  const allMessages = React.useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page) =>
      page.messages.map((msg) => ({
        isReceived: true,
        userName: msg.name,
        text: msg.message,
        time: msg.time,
        date: msg.date,
        user_id: msg.user_id,
        id: msg.message_id,
        reactions:
          msg.reactions?.map((r) => ({
            ...r,
            react_emoji:
              typeof r.react_emoji === "string"
                ? JSON.parse(r.react_emoji)
                : r.react_emoji,
          })) || [],
        file: msg.file_url,
        avatar: `${BASE_URL}${msg.emp_pic}`,
        source: "api",
        timestamp: getMessageTimestamp(msg.date, msg.time),
      }))
    );
  }, [data, BASE_URL, getMessageTimestamp]);
  // Combine all messages from all pages
  const combinedMessages = React.useMemo(() => {
    // Transform receiving socket messages
    const socketRecvMessages = (recivedMessageSocket || []).map((msg) => ({
      isReceived: true,
      userName: msg.name,
      text: msg.message,
      time: msg.time,
      date: msg.date,
      id: msg.message_id,
      user_id: msg.user_id,
      reactions:
        msg.reactions?.map((r) => ({
          ...r,
          react_emoji:
            typeof r.react_emoji === "string"
              ? JSON.parse(r.react_emoji)
              : r.react_emoji,
        })) || [],
      file: msg.file_url,
      avatar: `${BASE_URL}/${msg.emp_pic}`,
      source: "socket_receive",
      timestamp: getMessageTimestamp(msg.date, msg.time),
    }));
    // Transform sending socket messages
    const socketSendMessages = (chatSendingMessage || []).map((msg) => ({
      isReceived: false,
      userName: userDetails.name,
      text: msg.message,
      time: msg.time,
      date: msg.date,
      file: msg.file_url,
      id: msg.message_id,
      user_id: msg.user_id,
      reactions:
        msg.reactions?.map((r) => ({
          ...r,
          react_emoji:
            typeof r.react_emoji === "string"
              ? JSON.parse(r.react_emoji)
              : r.react_emoji,
        })) || [],
      avatar: `${BASE_URL}${msg.emp_pic}`,
      source: "socket_send",
      timestamp: getMessageTimestamp(msg.date, msg.time),
    }));

    // Combine all messages
    const allCombined = [
      ...(allMessages || []),
      ...socketSendMessages,
      ...socketRecvMessages,
    ];

    // Remove duplicates based on message ID
    const uniqueMessages = allCombined.reduce((acc, current) => {
      const existing = acc.find((msg) => msg.id === current.id);
      if (!existing) {
        acc.push(current);
      } else {
        // Prefer socket messages over API messages for real-time updates
        if (current.source.startsWith("socket")) {
          const index = acc.indexOf(existing);
          acc[index] = current;
        }
      }
      return acc;
    }, []);

    // Sort by timestamp
    return uniqueMessages.sort((a, b) => a.timestamp - b.timestamp);
  }, [
    allMessages,

    chatSendingMessage,
    recivedMessageSocket,
    BASE_URL,
    userDetails.name,
    getMessageTimestamp,
  ]);

  // Reset messages when chat changes
  useEffect(() => {
    if (selectedUser?.chat_unique !== currentChatRef.current) {
      // Clear socket messages when changing chats
      clearSendingMessage();
      clearRecivingMessage();
      currentChatRef.current = selectedUser?.chat_unique;
    }
  }, [selectedUser?.chat_unique, clearSendingMessage, clearRecivingMessage]);

  useEffect(() => {
    let heartbeat;

    const startHeartbeat = () => {
      heartbeat = setInterval(() => {
        setSkipLoader(true);
        setRefreshCount((prev) => prev + 1);
      }, 10000);
    };

    const stopHeartbeat = () => {
      if (heartbeat) clearInterval(heartbeat);
    };

    if (document.visibilityState === "visible") {
      startHeartbeat();
    }

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        startHeartbeat();
      } else {
        stopHeartbeat();
      }
    });

    return () => {
      stopHeartbeat();
      document.removeEventListener("visibilitychange", () => {});
    };
  }, []);

  const renderChatInput = () => {
    if (selectedUser?.active_status === "1") {
      return (
        <div className={styles.DisabledInput}>
          {" "}
          You are viewing the archives of a deactivated account
        </div>
      );
    }

    // Channel access restrictions
    if (selectedUser?.type === "channel") {
      if (selectedUser.allow_access === "only_manager") {
        const isManager = selectedUser.channel_manager?.includes(
          userDetails.id
        );
        if (!isManager) {
          return (
            <div className={styles.DisabledInput}>
              Only managers can send messages in this channel
            </div>
          );
        }
      }
    }

    // Default → show the real input
    return (
      <ChatInput
        text={text}
        setText={setText}
        uploadedFiles={uploadedFiles}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
        handleSend={handleSend}
        handleChange={handleChange}
        inputRef={inputRef}
        insertMentionUser={insertMentionUser}
        onHeightChange={setHeight}
      />
    );
  };

  const handleWorkspaceUpdate = useCallback(() => {
    setRefreshCount((prev) => prev + 1);
    // refetchMessages();
    // setRefreshConv((prev) => prev + 1);
    setPendingMessages(false);
    setTyping("");
  }, [setPendingMessages, setTyping]);

  const handleHeaderClick = () => {
    if (selectedUser?.is_group_chat === "channel") {
      setShowChannelPopup(true);
    } else if (selectedUser?.is_group_chat === "1") {
      setShowGroupPopup(true);
    } else {
      setShowPersonPopUp(true);
    }
  };
  const handleUpdated = () => {
    setRefreshCount((prev) => prev + 1); // Trigger refresh
  };
  const onSelectConversation = useCallback(
    (data) => {
      WebSocketService.disconnect();

      storeSelectedPerson({
        ...data,
        type: "dm",
      });

      setPendingMessages(false); // clear pending messages
      clearSendingMessage(); // clear unsent messages
      clearRecivingMessage(); // clear incoming messages
      setText([]); // reset text
      setUploadedFiles([]); // reset uploaded files
      setRefreshCount((prev) => prev + 1);
      setRefreshChat((prev) => prev + 1);
      setTyping(""); // clear typing status
    },
    [
      clearSendingMessage,
      clearRecivingMessage,
      setText,
      setUploadedFiles,
      setPendingMessages,
      setTyping,
    ]
  );

  useEffect(() => {
    WebSocketOnline.callbacks.onmessage = (data) => {
      if (data && data.type === "msg_count_update") {
        setSkipLoader(true);
        setRefreshCount((prev) => prev + 1);
      }
    };
  }, []); // Add userDetails to dependencies

  useEffect(() => {
    const handleFocus = () => {
      setSkipLoader(true); // next refresh should skip loader

      setRefreshCount((prev) => prev + 1);
      // setRefreshConv((prev) => prev + 1);
      // clearSendingMessage();
      // clearRecivingMessage();
      setTyping("");
    };
    // clearSendingMessage();
    // clearRecivingMessage();

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [clearSendingMessage, clearRecivingMessage, setTyping]);

  const onSelectedChannels = useCallback(
    (data) => {
      WebSocketService.disconnect();

      setPendingMessages(false);
      setText([]);
      setUploadedFiles([]);
      clearSendingMessage();
      clearRecivingMessage();

      storeSelectedPerson({
        ...data,
        type: "channel",
      });
      setRefreshCount((prev) => prev + 1);
      // setRefreshConv((prev) => prev + 1);
    },
    [
      clearSendingMessage,
      clearRecivingMessage,
      setText,
      setUploadedFiles,
      setPendingMessages,
    ]
  );

  const storeScrollPosition = useCallback(() => {
    if (
      chatViewRef.current &&
      !isScrollingProgrammaticallyRef.current &&
      initialLoadCompleteRef.current
    ) {
      const chatContainer = chatViewRef.current;
      scrollPositionRef.current =
        chatContainer.scrollHeight - chatContainer.scrollTop;
      previousMessagesLengthRef.current = combinedMessages.length;
    }
  }, [combinedMessages.length]);

  const restoreScrollPosition = useCallback(() => {
    if (
      chatViewRef.current &&
      scrollPositionRef.current > 0 &&
      initialLoadCompleteRef.current
    ) {
      isScrollingProgrammaticallyRef.current = true;
      const chatContainer = chatViewRef.current;
      const newScrollHeight = chatContainer.scrollHeight;
      const newScrollTop = newScrollHeight - scrollPositionRef.current;

      chatContainer.scrollTop = newScrollTop;

      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
      }, 100);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (chatViewRef.current && combinedMessages.length > 0) {
      isScrollingProgrammaticallyRef.current = true;
      const chatContainer = chatViewRef.current;

      setTimeout(() => {
        chatContainer.scrollTop = chatContainer.scrollHeight;

        setTimeout(() => {
          isScrollingProgrammaticallyRef.current = false;
          initialLoadCompleteRef.current = true;
        }, 100);
      }, 100);
    }
  }, [combinedMessages.length]);

  // Store scroll position when user scrolls
  useEffect(() => {
    const chatContainer = chatViewRef?.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      if (
        !isScrollingProgrammaticallyRef.current &&
        initialLoadCompleteRef.current
      ) {
        storeScrollPosition();
      }
    };

    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, [storeScrollPosition]);

  // Handle initial load and chat changes
  useEffect(() => {
    if (combinedMessages.length > 0) {
      if (isInitialLoad || !initialLoadCompleteRef.current) {
        // First load or new chat - scroll to bottom
        scrollToBottom();
        setIsInitialLoad(false);
      } else if (combinedMessages.length > previousMessagesLengthRef.current) {
        // New messages loaded at top (infinite scroll)
        restoreScrollPosition();
        previousMessagesLengthRef.current = combinedMessages.length;
      } else if (combinedMessages.length < previousMessagesLengthRef.current) {
        // Chat changed completely
        previousMessagesLengthRef.current = combinedMessages.length;
        scrollPositionRef.current = 0;
        initialLoadCompleteRef.current = false;
        setIsInitialLoad(true);
      }
    }
  }, [
    combinedMessages.length,
    restoreScrollPosition,
    scrollToBottom,
    isInitialLoad,
  ]);

  const { ref: topObserverRef, inView: topInView } = useInView({
    threshold: 0.1,
    rootMargin: "50px",
    skip: !hasNextPage || isFetchingNextPage || loadingRef.current,
  });

  // Debounced load more function
  const loadMoreMessages = useCallback(async () => {
    if (loadingRef.current || !hasNextPage || isFetchingNextPage) {
      return;
    }

    // Store current scroll position before loading
    storeScrollPosition();

    loadingRef.current = true;
    setIsLoadingMore(true);

    try {
      await fetchNextPage();
    } catch (error) {
      console.error("Error loading more messages:", error);
    } finally {
      // Add a small delay to prevent rapid consecutive calls
      setTimeout(() => {
        loadingRef.current = false;
        setIsLoadingMore(false);
      }, 500);
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, storeScrollPosition]);

  // Load more messages when scrolling to top - with proper conditions
  useEffect(() => {
    if (
      topInView &&
      hasNextPage &&
      !isFetchingNextPage &&
      !loadingRef.current &&
      initialLoadCompleteRef.current
    ) {
      loadMoreMessages();
    }
  }, [topInView, hasNextPage, isFetchingNextPage, loadMoreMessages]);

  // Reset states when chat changes
  useEffect(() => {
    loadingRef.current = false;
    setIsLoadingMore(false);
    scrollPositionRef.current = 0;
    previousMessagesLengthRef.current = 0;
    isScrollingProgrammaticallyRef.current = false;
    initialLoadCompleteRef.current = false;
    setIsInitialLoad(true);
  }, [selectedUser?.chat_unique]);

  // Auto-scroll to bottom for new incoming messages at bottom
  useEffect(() => {
    if (
      !chatViewRef.current ||
      isScrollingProgrammaticallyRef.current ||
      !initialLoadCompleteRef.current ||
      isInitialLoad
    ) {
      return;
    }

    const chatContainer = chatViewRef.current;
    const isNearBottom =
      chatContainer.scrollHeight -
        chatContainer.scrollTop -
        chatContainer.clientHeight <
      100;

    // Only auto-scroll if user is near bottom and it's not a load more operation
    if (isNearBottom) {
      isScrollingProgrammaticallyRef.current = true;
      chatContainer.scrollTop = chatContainer.scrollHeight;

      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
      }, 100);
    }
  }, [combinedMessages.length, isInitialLoad]);

  return (
    <>
      <ChatLayout
        sidebar={<SideBar onWorkspaceUpdate={handleWorkspaceUpdate} />}
        chatPannel={
          <HomePannel
            channels={channels}
            directMessages={directMessages}
            loading={loading}
            error={error}
            onSelectConversation={onSelectConversation}
            onSelectedChannels={onSelectedChannels}
          />
        }
        mainContent={
          <div className={styles.ChatPageMainContent}>
            <div className={styles.ChatPageMainContentSub}>
              {selectedUser ? (
                <>
                  <ChatHeader
                    userName={userName}
                    status={onlineStatus}
                    userImage={userImage}
                    typing={typing}
                    onHeaderClick={handleHeaderClick}
                    type={selectedUser?.is_group_chat}
                    onSearch={handleSearch} // Pass search handler
                    searchQuery={searchQuery} // Pass current search query
                    pageFrom={"home"}
                  />
                  <div className={styles.ChatPageMessageInput}>
                    <ChatView
                      messages={combinedMessages} // Use combinedMessages instead of allMessages
                      chatViewRef={chatViewRef}
                      isLoading={loadingConv}
                      height={height}
                      newReaction={reactions}
                      // loading={isSending}
                      pendingMessages={pendingMessages}
                      chat_unique={selectedUser?.chat_unique}
                      typing={typing}
                      lastMessageCount={lastMessageCount}
                      setLastMessageCount={setLastMessageCount}
                      // Pass to ChatView
                      topObserverRef={topObserverRef}
                      isFetchingNextPage={isFetchingNextPage || isLoadingMore}
                      hasNextPage={hasNextPage} // Add this prop
                      onScrollPositionChange={storeScrollPosition}
                    />
                    {renderChatInput()}
                  </div>
                </>
              ) : (
                <>
                  <NoConvFound />
                </>
              )}
            </div>
          </div>
        }
      />
      {showChannelPopup && (
        <ViewChannelPopUp
          onClose={() => setShowChannelPopup(false)}
          channelId={channelId}
          updated={handleUpdated}
        />
      )}{" "}
      {showGroupPopup && (
        <ViewGroupPopUp
          onClose={() => setShowGroupPopup(false)}
          groupId={groupId}
          updated={handleUpdated}
        />
      )}
      {showPersonPopUp && (
        <PersonQuickView
          userId={userId}
          onClose={() => setShowPersonPopUp(false)}
        />
      )}
    </>
  );
};

export default HomePage;
